
"use client";

import { useEffect, useRef, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * RUNYI central VALUE notification plane, NOT a second database, permission
 * boundary or provider-truth register. Reconcile by re-reading authoritative
 * OWNER ROOT data through the consumer's existing authorized API/RPC.
 *
 * V3 listens to RUNYI's actor-scoped UI refresh events. Backend source changes
 * resolve into canonical domains/screens/blocks first; the browser only
 * reconciles affected consumers. There is no global revision fanout and no
 * unbounded browser polling. A visible-screen watchdog catches lost events.
 */
type Domain =
  | "catalogue"
  | "infrastructure"
  | "applications"
  | "projects"
  | "identity"
  | "ai"
  | "explorer"
  | "workspace"
  | "telemetry"
  | "all";

type EventData = {
  domains: Domain[];
  origin: "backend" | "mutation" | "manual";
  screenKey?: string;
  blocks?: string[];
};

type UiRefreshRow = {
  event_type?: string | null;
  changed_domains?: unknown;
  changed_blocks?: unknown;
  screen_key?: string | null;
};

export type ValueSyncState = "CURRENT" | "SYNCING" | "STALE" | "ERROR";

const EVENT_NAME = "runyi:canonical-value-changed";
const channels = new WeakMap<SupabaseClient, { count: number; teardown: () => void }>();

const backendDomainMap: Record<string, Domain[]> = {
  CAPABILITIES: ["catalogue"],
  RESOURCES: ["infrastructure", "explorer"],
  INFRASTRUCTURE: ["infrastructure", "projects"],
  APPLICATIONS: ["applications"],
  ACCOUNTS: ["identity"],
  PROVIDERS: ["catalogue", "infrastructure", "ai"],
  BOT: ["ai", "workspace"],
  AI_USAGE: ["ai"],
  AUTOMATION: ["ai", "workspace"],
  ARCHITECTURE: ["all"],
  NAVIGATION: ["all"],
  UI_CONTROL: ["all"],
  TELEMETRY: ["telemetry", "infrastructure"],
};

const screenDomainMap: Record<string, Domain[]> = {
  BOT_WORKSPACE: ["ai", "workspace"],
  AI_USAGE: ["ai"],
  MARKETPLACE: ["catalogue", "infrastructure", "telemetry"],
  NEW_INFRASTRUCTURE: ["infrastructure", "projects"],
  APPS: ["applications"],
  EXPLORER: ["explorer", "infrastructure"],
  ACCOUNTS_SETUP: ["identity"],
  SETTINGS: ["all"],
};

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string" && item.trim().length > 0)
    : [];
}

function projectBackendDomains(changedDomains: unknown, screenKey?: string | null): Domain[] {
  const projected = new Set<Domain>();
  for (const raw of stringArray(changedDomains)) {
    const key = raw.trim().toUpperCase();
    const mapped = backendDomainMap[key];
    if (!mapped) {
      projected.add("all");
      continue;
    }
    for (const domain of mapped) projected.add(domain);
  }

  if (projected.size === 0 && screenKey) {
    for (const domain of screenDomainMap[String(screenKey).toUpperCase()] ?? ["all"]) {
      projected.add(domain);
    }
  }

  return projected.size > 0 ? [...projected] : ["all"];
}

export function publishRunyiValueChange(
  domains: Domain[] = ["all"],
  origin: EventData["origin"] = "mutation",
  metadata: Pick<EventData, "screenKey" | "blocks"> = {},
) {
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<EventData>(EVENT_NAME, {
        detail: { domains, origin, ...metadata },
      }),
    );
    window.dispatchEvent(
      new CustomEvent("runyi:ui-projection:invalidate", {
        detail: { domains, origin, ...metadata },
      }),
    );
  }
}

function acquireSharedFeed(supabase: SupabaseClient) {
  const existing = channels.get(supabase);
  if (existing) {
    existing.count++;
    return () => releaseSharedFeed(supabase);
  }

  const channel = supabase
    .channel("runyi-central-ui-refresh-events")
    .on(
      "postgres_changes",
      { schema: "public", table: "runyi_ui_refresh_events", event: "INSERT" },
      (payload) => {
        const row = (payload.new ?? {}) as UiRefreshRow;
        if (String(row.event_type ?? "").toUpperCase() !== "SNAPSHOT_READY") return;

        publishRunyiValueChange(
          projectBackendDomains(row.changed_domains, row.screen_key),
          "backend",
          {
            screenKey: row.screen_key ? String(row.screen_key) : undefined,
            blocks: stringArray(row.changed_blocks),
          },
        );
      },
    );

  channel.subscribe();
  channels.set(supabase, {
    count: 1,
    teardown: () => {
      void supabase.removeChannel(channel);
    },
  });
  return () => releaseSharedFeed(supabase);
}

function releaseSharedFeed(supabase: SupabaseClient) {
  const feed = channels.get(supabase);
  if (!feed) return;
  feed.count--;
  if (feed.count <= 0) {
    channels.delete(supabase);
    feed.teardown();
  }
}

export function useRunyiValueSync({
  supabase,
  connected,
  domains,
  onRefresh,
  intervalMs = 30_000,
}: {
  supabase: SupabaseClient | null;
  connected: boolean;
  domains: Domain[];
  onRefresh: () => void | Promise<unknown>;
  intervalMs?: number;
}) {
  const [state, setState] = useState<ValueSyncState>("STALE");
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const callback = useRef(onRefresh);
  const hasSynced = useRef(false);
  callback.current = onRefresh;
  const domainKey = domains.join("|");

  useEffect(() => {
    if (!supabase || !connected) {
      hasSynced.current = false;
      setState("STALE");
      setLastSyncedAt(null);
      return;
    }

    const dispose = acquireSharedFeed(supabase);
    let active = true;
    let pending: number | undefined;
    let inFlight = false;
    let replay = false;
    const domainsList = domainKey.split("|");

    const reconcile = async () => {
      if (!active || document.visibilityState !== "visible") return;
      if (inFlight) {
        replay = true;
        return;
      }
      if (!navigator.onLine) {
        if (active) setState("STALE");
        return;
      }

      inFlight = true;
      if (active && !hasSynced.current) setState("SYNCING");

      try {
        const result = await callback.current();
        if (result === false) throw new Error("RUNYI source authority refresh did not succeed");
        if (active) {
          hasSynced.current = true;
          setLastSyncedAt(new Date().toISOString());
          setState("CURRENT");
        }
      } catch {
        if (active) setState(hasSynced.current ? "STALE" : "ERROR");
      } finally {
        inFlight = false;
        if (replay && active) {
          replay = false;
          schedule(100);
        }
      }
    };

    const schedule = (delay = 75) => {
      if (pending !== undefined) window.clearTimeout(pending);
      pending = window.setTimeout(() => {
        pending = undefined;
        void reconcile();
      }, delay);
    };

    const event = (raw: Event) => {
      const detail = (raw as CustomEvent<EventData>).detail;
      if (
        !detail ||
        detail.domains.includes("all") ||
        domainsList.includes("all") ||
        detail.domains.some((domain) => domainsList.includes(domain))
      ) {
        schedule();
      }
    };

    const foreground = () => {
      if (document.visibilityState === "visible" && navigator.onLine) schedule(75);
    };

    const offline = () => {
      if (active) setState("STALE");
    };

    window.addEventListener(EVENT_NAME, event);
    document.addEventListener("visibilitychange", foreground);
    window.addEventListener("focus", foreground);
    window.addEventListener("online", foreground);
    window.addEventListener("offline", offline);

    // Events remain primary. The existing shared coordination hook also bounds
    // reconciliation after missed delivery, without reloading the WebView.
    const cadence = Number.isFinite(intervalMs) ? Math.max(15_000, intervalMs) : 30_000;
    const watchdog = window.setInterval(foreground, Math.min(cadence, 300_000));
    schedule(25);

    return () => {
      active = false;
      window.clearInterval(watchdog);
      if (pending !== undefined) window.clearTimeout(pending);
      window.removeEventListener(EVENT_NAME, event);
      document.removeEventListener("visibilitychange", foreground);
      window.removeEventListener("focus", foreground);
      window.removeEventListener("online", foreground);
      window.removeEventListener("offline", offline);
      dispose();
    };
  }, [supabase, connected, domainKey, intervalMs]);

  return { state, lastSyncedAt };
}

